export const GRID_SLOTS = 20;
export const HOTBAR_SLOT = 0;
export const STACK_RESOURCE = 20;
export const STACK_FOOD = 5;
export const STACK_WEAPON = 1;

export type InvSlot = { id: string; count: number };

/** Unique holdables. Spear stays stackLimit 1. */
export const HOLDABLE = [
  'spear',
  'bow',
  'torch',
  'shovel',
  'rope',
  'tracker',
  'axe',
  'metal_axe',
  'sling',
  'compass',
] as const;

export type HoldableId = (typeof HOLDABLE)[number];

export function isHoldable(id: string): boolean {
  return (HOLDABLE as readonly string[]).includes(id);
}

export function isWeapon(id: string): boolean {
  return id === 'spear' || id === 'bow';
}

export function stackLimit(id: string): number {
  if (id === 'wood' || id === 'fiber' || id === 'leather') {
    return STACK_RESOURCE;
  }
  if (id === 'raw' || id === 'cooked') {
    return STACK_FOOD;
  }
  if (isWeapon(id) || isHoldable(id)) {
    return STACK_WEAPON;
  }
  return STACK_WEAPON;
}

export class Grid {
  readonly slots: (InvSlot | null)[];

  constructor(size = GRID_SLOTS) {
    this.slots = Array.from({ length: size }, () => null);
  }

  get size(): number {
    return this.slots.length;
  }

  countOf(id: string): number {
    let total = 0;
    for (const slot of this.slots) {
      if (slot && slot.id === id) {
        total += slot.count;
      }
    }
    return total;
  }

  /** True when at least `count` of `id` were removed. */
  remove(id: string, count = 1): boolean {
    if (count <= 0 || !id) {
      return count <= 0;
    }
    if (this.countOf(id) < count) {
      return false;
    }
    let left = count;
    for (let i = this.slots.length - 1; i >= 0 && left > 0; i--) {
      const slot = this.slots[i];
      if (!slot || slot.id !== id) {
        continue;
      }
      const take = Math.min(left, slot.count);
      slot.count -= take;
      left -= take;
      if (slot.count <= 0) {
        this.slots[i] = null;
      }
    }
    return true;
  }

  /** Returns leftover that did not fit. */
  add(id: string, count = 1): number {
    if (count <= 0 || !id) {
      return Math.max(0, count);
    }
    const limit = stackLimit(id);
    if (limit === 1 && this.countOf(id) >= 1) {
      return count;
    }
    let left = count;
    left = this.fillExisting(id, left, limit);
    if (left <= 0) {
      return 0;
    }
    return this.fillEmpty(id, left, limit);
  }

  /** Place or move `id` into a hotbar slot. Creates the stack if missing. */
  equip(id: string, slot = HOTBAR_SLOT): boolean {
    if (slot < 0 || slot >= this.slots.length) {
      return false;
    }
    if (this.countOf(id) <= 0 && this.add(id, 1) !== 0) {
      return false;
    }
    const have = this.indexOf(id);
    if (have < 0) {
      return false;
    }
    if (have === slot) {
      return true;
    }
    const moving = this.slots[have];
    if (!moving) {
      return false;
    }
    this.slots[have] = this.slots[slot] ?? null;
    this.slots[slot] = moving;
    return true;
  }

  held(slot = HOTBAR_SLOT): string | null {
    return this.slots[slot]?.id ?? null;
  }

  stacks(): InvSlot[] {
    const out: InvSlot[] = [];
    for (const slot of this.slots) {
      if (slot) {
        out.push({ id: slot.id, count: slot.count });
      }
    }
    return out;
  }

  replace(stacks: InvSlot[]): void {
    for (let i = 0; i < this.slots.length; i++) {
      this.slots[i] = null;
    }
    for (const stack of stacks) {
      if (stack?.id && stack.count > 0) {
        this.add(stack.id, stack.count);
      }
    }
  }

  private indexOf(id: string): number {
    return this.slots.findIndex((slot) => slot !== null && slot.id === id);
  }

  private fillExisting(id: string, left: number, limit: number): number {
    for (let i = 0; i < this.slots.length && left > 0; i++) {
      const slot = this.slots[i];
      if (!slot || slot.id !== id || slot.count >= limit) {
        continue;
      }
      const take = Math.min(left, limit - slot.count);
      slot.count += take;
      left -= take;
    }
    return left;
  }

  private fillEmpty(id: string, left: number, limit: number): number {
    for (let i = 0; i < this.slots.length && left > 0; i++) {
      if (this.slots[i]) {
        continue;
      }
      const take = Math.min(left, limit);
      this.slots[i] = { id, count: take };
      left -= take;
    }
    return left;
  }
}
